import React, { useCallback, useEffect, useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Card,
  InputNumber,
  Select,
  Divider,
  Typography,
  Row,
  Col,
  Space,
  AutoComplete,
  Tag,
  notification,
  Spin,
  Modal,
  Checkbox,
  Radio,
} from 'antd';
import type { FormListFieldData } from 'antd/es/form';
import { PlusOutlined } from '@ant-design/icons';
import ApiService from 'src/services/api-service';
import { debounce } from 'lodash';
import useSales from 'src/hooks/use-sales';
import { ProductInfo, SaleInfo, WorkerInfo } from 'src/types/common.types';
import useProducts from 'src/hooks/use-products';
import useWorkers from 'src/hooks/use-workers';
import { removeAccentCharacters } from 'src/utils/helpers';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import SiriInspiredLoading from 'src/components/Loading';
import useCustomerSources from 'src/hooks/use-customer-sources';
import useWorkItems from 'src/hooks/use-work-items';
import { CalendarCog, CreditCard, DollarSign, ListPlus, OctagonMinus, ShoppingBasket, UserPen } from 'lucide-react';
import TokenService from 'src/services/token';
import { fulfillmentMethodOptions, workItemUnitOptions } from './entry.constants';
import { calculateVATAmount, formatCurrency, getFilledProducts, hasAtLeastOneProduct } from './entry.utils';
import { Customer, OrderFinancialInfo, OutboundModalItem, ProductItem } from './entry.types';
import { useOutboundImport } from './use-outbound-import';
import { OutboundImportModal, OutboundImportSection, OutboundImportTags } from './outbound-import-ui';
import { getProductTableColumns } from './product-table-columns';
import { getPaymentTableColumns } from './payment-table-columns';

const { Title, Text } = Typography;
const { TextArea } = Input;

const OrderCreationForm: React.FC = () => {
  const user = TokenService.getUser();

  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.id;
  let type = params.type;
  const isUpdateOrder = orderId != 'entry';
  const [form] = Form.useForm();
  const [orderInfo, setOrderInfo] = useState();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [orders, setOrders] = useState([]);
  const [orderChangedLog, setOrderChangedLog] = useState<any[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [orderFinancialInfo, setOrderFinancialInfo] = useState<OrderFinancialInfo>({
    totalProductValue: 0,
    totalAmount: 0,
    totalVatAmount: 0,
    netAmount: 0,
    grossAmount: 0,
  });

  const { data: sales, error: errorSales, isLoading: fetchSaleLoading } = useSales();
  const { data: products, error: errorProducts, isLoading: fetchProductLoading } = useProducts();
  const {
    data: workers,
    error: errorWorkers,
    isLoading: fetchWorkerLoading,
  } = useWorkers({ includeWorkerPolicy: true });
  const {
    data: customerSources,
    error: errorCustomerSources,
    isLoading: fetchCustomerSourcesLoading,
  } = useCustomerSources();
  const { data: workItems, error: errorWorkItems, isLoading: fetchWorkItemsLoading } = useWorkItems();
  const [customers, setCustomers] = useState<Customer[]>([]);

  const backToOrderPage = () => {
    navigate('/orders');
  };

  const searchCustomers = async (keyword: string = '') => {
    if (!keyword) {
      setCustomers([]);
      return;
    }
    const response = await ApiService.get('/api/customers/search', {
      params: {
        keyword,
      },
    });

    setCustomers(response.data);
  };

  const searchOrders = useCallback(
    debounce(async value => {
      if (!value) {
        setOrders([]);
        return;
      }
      try {
        const response = await ApiService.get('/api/orders/search', {
          params: {
            keyword: value?.replace(/\D/g, ''),
          },
        });
        setOrders(response.data);
      } catch (error) {
        notification.error({
          message: 'Lỗi khi tìm kiếm đơn hàng',
        });
      }
    }, 300),
    [],
  );

  const onSelectOrder = async (value: number, option: any) => {
    try {
      const orderInfo = await getOrder(value);
      form.setFieldsValue({
        address: orderInfo.address,

        customerName: orderInfo.customerInfo.name,
        customerPhone: orderInfo.customerInfo.phoneNumber,
        customerAddress: orderInfo.customerInfo.address,
        customerSource: orderInfo.customerInfo.source,
        products: orderInfo.orderItems?.map((item: any) => {
          return {
            productId: item.productId,
            code: item.productInfo.code,
            name: item.productInfo.name,
            quantity: item.quantity,
            minPrice: item.productInfo?.minPrice,
            maxPrice: item.productInfo?.maxPrice,
            price: 0,
          };
        }),
        totalProductValue: 0,
        vatAmount: 0,
        vat: orderInfo.vat,
        refOrderId: orderInfo.id,
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi lấy thông tin đơn hàng',
      });
    }
  };

  async function getOrder(orderId: number) {
    try {
      setLoading(true);
      const response = await ApiService.get(`/api/orders/${orderId}`);
      if (response.status != 200) {
        return null;
      }
      const data = response.data;
      return data;
    } catch (error) {
      throw new Error('Không lấy được thông tin đơn hàng');
    } finally {
      setLoading(false);
    }
  }

  async function getOrderChangedLog(orderId: number) {
    try {
      setLoading(true);
      const response = await ApiService.get(`/api/orders/${orderId}/logs`);
      if (response.status != 200) {
        return null;
      }
      const data = response.data;
      return data;
    } catch (error) {
      throw new Error('Không lấy được thông tin lịch sử đơn hàng');
    } finally {
      setLoading(false);
    }
  }

  const formProducts = Form.useWatch('products', form) || [];
  const workersSelected = Form.useWatch('workers', form) || [];

  const onFormValuesChange = debounce((changedValues: any, allValues: any) => {
    let amount = 0;
    let vatAmount = 0;
    allValues.products.forEach((product: ProductItem) => {
      const { quantity = 0, price = 0, vat = 0 } = product || {};
      const total = quantity * price;
      amount += total;
      const totalVAT = calculateVATAmount(total, vat);
      product.vatAmount = totalVAT;
      vatAmount += totalVAT;
    });
    form.setFieldsValue({
      totalProductValue: amount,
      vatAmount: vatAmount,
    });
    setOrderFinancialInfo(prev => ({
      ...prev,
      totalProductValue: amount,
      totalVatAmount: vatAmount,
      netAmount: amount - vatAmount - (allValues.commissionFee || 0),
      grossAmount: amount + (allValues.ktvTip || 0) + (allValues.additionalFee || 0),
    }));
  }, 200);

  const outboundImport = useOutboundImport({
    form,
    products,
    formProducts,
    onFormValuesChange,
  });

  const {
    outboundInputId,
    setOutboundInputId,
    importedOutboundTags,
    outboundModalOpen,
    setOutboundModalOpen,
    outboundModalItems,
    setOutboundModalItems,
    outboundLoading,
    pendingOutboundId,
    existingSkuQuantities,
    isDuplicateOutboundSku,
    handleImportOutbound,
    handleConfirmOutboundImport,
    handleRemoveOutboundTag,
    validateOutboundTagsBeforeSave,
    resetOutboundImport,
  } = outboundImport;

  async function getUpdateOrderInfo() {
    const data = await getOrder(Number(orderId));
    if (!data) {
      notification.error({
        message: 'Không lấy được thông tin đơn hàng',
      });
      return;
    }
    if (data.invoiceInfo) {
      data.isAddInvoiceInfo = true;
    } else {
      data.isAddInvoiceInfo = false;
    }
    const outboundRequestId = outboundImport.loadOutboundTagsFromOrder(data);

    form.setFieldsValue({
      ...data,
      outboundRequestId,
      customerName: data.customerInfo.name,
      customerPhone: data.customerInfo.phoneNumber,
      customerAddress: data.customerInfo.address,
      orderDate: dayjs(data.orderDate),
      workers: data.workers?.map((worker: any) => worker.workerId),
      products: data.orderItems?.map((item: any) => {
        return {
          id: item.id,
          productId: item.productId,
          code: item.productInfo.code,
          name: item.productInfo.name,
          minPrice: item.productInfo?.minPrice,
          maxPrice: item.productInfo?.maxPrice,
          vat: item.vat || 0,
          vatAmount: item.vatAmount || 0,
          quantity: item.quantity,
          price: item.price,
          workItems: item.orderItemTasks?.map((workItem: any) => {
            return {
              ...workItem,
            };
          }),
        };
      }),
      sharedSales: data.sharedSales?.map((item: any) => item.saleId),
      totalProductValue: data.amount,
      vatAmount: data.vatAmount,
      payments:
        data.orderPayments?.map((item: any) => {
          return {
            id: item.id,
            date: dayjs(item.date),
            amount: item.amount,
            method: item.method,
            note: item.note,
            status: item.status,
          };
        }) ?? [],
    });
    setOrder(data);
    setOrderFinancialInfo({
      totalProductValue: data.amount,
      totalAmount: data.amount,
      totalVatAmount: data.vatAmount,
      netAmount: data.amount - data.vatAmount - (data.commissionFee || 0),
      grossAmount: data.amount + (data.ktvTip || 0) + (data.additionalFee || 0),
    });
    if (user.role == 1) {
      try {
        const orderChangedLog = await getOrderChangedLog(data.id);
        if (orderChangedLog) {
          setOrderChangedLog(orderChangedLog);
        }
      } catch (error) {
        notification.error({
          message: 'Không lấy được thông tin lịch sử đơn hàng',
        });
      }
    }
  }

  useEffect(() => {
    searchCustomers();
    if (isUpdateOrder) {
      getUpdateOrderInfo();
    }
  }, []);

  const onSearchSelect = (keyword: string, option: any) => {
    return option.props.searchname.includes(removeAccentCharacters(keyword).toLowerCase().trim());
  };

  const onSearchWorker = onSearchSelect;
  const onSearchWorkItem = onSearchSelect;
  const onSearchSale = onSearchSelect;
  const onSearchProduct = onSearchSelect;
  const onSearchProductBySku = (keyword: string, option: any) => {
    return removeAccentCharacters(option.props.code.toLowerCase())
      .replace(/ /g, '')
      .includes(removeAccentCharacters(keyword.replace(/ /g, '')).toLowerCase().trim());
  };

  const onSelectProduct = (index: number, value: number, option: any) => {
    const values = form.getFieldsValue();
    const orderProducts = values.products;
    const product = orderProducts[index];
    product.name = option.name;
    product.code = option.code;
    product.price = product.price || option.price;
    product.productId = option.value;
    product.index = index;
    product.vat = option.vat;
    product.minPrice = option.minPrice;
    product.maxPrice = option.maxPrice;
    form.setFieldsValue({
      ...values,
      products: orderProducts,
    });
    // setProductOptions(products);
  };

  const onSelectWorkItem = (indexProduct: number, indexWorkItem: number, value: number, option: any) => {
    const values = form.getFieldsValue();
    const orderProducts = values.products;
    const product = orderProducts[indexProduct];
    const workItem = product.workItems[indexWorkItem];
    workItem.coefficient = option.estimateCost;
    workItem.workItemId = option.value;
    workItem.unit = option.unitNum;
    if (workItem.workerId) {
      const worker = workers?.find((worker: WorkerInfo) => worker.id == workItem.workerId);
      if (worker && !workItem.cost) {
        let baseCost = 0;
        workItem.workPolicyId = worker.workerPolicy?.workPolicy?.id || 0;
        switch (workItem.unit) {
          case 1:
            baseCost = worker.workerPolicy?.workPolicy?.costInstall || 0;
            break;
          case 2:
            baseCost = worker.workerPolicy?.workPolicy?.costWarranty || 0;
            break;
          case 3:
            break;
        }
        workItem.cost = baseCost * workItem.coefficient;
      }
    }
    form.setFieldsValue({
      ...values,
      products: orderProducts,
    });
  };
  const onCancelOrder = () => {
    Modal.confirm({
      title: 'Hủy đơn hàng',
      content: 'Bạn có chắc chắn muốn hủy đơn hàng này không?',
      onOk: async () => {
        try {
          const response = await ApiService.put(`/api/orders/${orderId}/cancel`);
          if (response.status != 200) {
            throw new Error('Lỗi khi hủy đơn hàng');
          }
          notification.success({
            message: 'Hủy đơn hàng thành công',
          });
          navigate('/orders');
        } catch (error) {
          notification.error({
            message: 'Lỗi khi hủy đơn hàng',
          });
        }
      },
      okText: 'Xác nhận',
      cancelText: 'Quay lại',
    });
  };
  const productColumns = (fields: FormListFieldData[], remove: (index: number) => void) =>
    getProductTableColumns({
      products,
      formProducts,
      onSearchProductBySku,
      onSearchProduct,
      onSelectProduct,
      remove,
    })(fields, remove);

  const paymentColumns = (fields: FormListFieldData[], remove: (index: number) => void) =>
    getPaymentTableColumns(remove)(fields);

  const onSelectCustomer = (value: number, option: any) => {
    const values = form.getFieldsValue();
    values.customerId = option.customerId;
    values.leadId = option.leadId;
    values.customerPhone = option.phone;
    values.customerName = option.name;
    values.customerAddress = option.address;
    values.customerSource = option.source;

    form.setFieldsValue({
      ...values,
    });
  };
  const onSearchCustomer = useCallback(
    debounce(async value => {
      if (!value) {
        setCustomers([]);
        return;
      }
      try {
        searchCustomers(value);
      } catch (error) {}
    }, 300),
    [],
  );

  const validateProductsBeforeSave = (): boolean => {
    if (!hasAtLeastOneProduct(form.getFieldValue('products'))) {
      notification.error({
        message: 'Đơn hàng thiếu sản phẩm',
        description: 'Vui lòng thêm ít nhất một sản phẩm trước khi lưu đơn hàng.',
      });
      return false;
    }
    return true;
  };

  const handleFormSubmit = async () => {
    if (loading) {
      return false;
    }
    if (!validateOutboundTagsBeforeSave()) {
      return false;
    }
    if (!validateProductsBeforeSave()) {
      return false;
    }
    try {
      setLoading(true);
      const values = await form.validateFields();
      values.orderDate = values.orderDate.format('YYYY-MM-DD');
      values.payments?.map((payment: any) => {
        if (payment.date) {
          payment.date = payment.date.format('YYYY-MM-DD');
        }
      });
      const data = {
        ...values,
        products: getFilledProducts(values.products),
      };
      data.amount = 0;
      data.products.forEach((product: ProductItem) => {
        product.amount = product.quantity * product.price;
        data.amount += product.amount;
      });

      for (const payment of data.payments) {
        if (payment.method === 'other') {
          payment.note = payment.note || '';
        } else {
          payment.note = '';
        }
      }

      const response = await ApiService.post('/api/orders', data);
      if (response.status != 200) {
        return false;
      }
      notification.success({
        message: 'Tạo đơn hàng thành công',
      });
      return true;
    } catch (error: any) {
      if (error.errorFields) {
        notification.error({
          message: 'Thiếu thông tin đơn hàng',
          description: error.errorFields.map((field: any) => `${field.errors.join(', ')}`).join('\n'),
        });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onSubmitAndCreate = async () => {
    const success = await handleFormSubmit();
    if (success) {
      form.resetFields();
      resetOutboundImport();
      setOrderFinancialInfo({
        totalProductValue: 0,
        totalAmount: 0,
        totalVatAmount: 0,
        netAmount: 0,
        grossAmount: 0,
      });
    } else {
      notification.error({
        message: 'Tạo đơn hàng thất bại',
      });
    }
  };

  const onSubmit = async () => {
    const success = await handleFormSubmit();
    if (success) {
      navigate('/orders');
    } else {
      notification.error({
        message: 'Tạo đơn hàng thất bại',
      });
    }
  };

  const onUpdateOrder = async () => {
    if (!validateOutboundTagsBeforeSave()) {
      return false;
    }
    if (!validateProductsBeforeSave()) {
      return false;
    }
    const values = await form.validateFields();
    const data = {
      ...values,
      orderDate: values.orderDate.format('YYYY-MM-DD'),
      products: getFilledProducts(values.products),
    };

    data.amount = 0;
    data.products.forEach((product: ProductItem) => {
      product.amount = product.quantity * product.price;
      data.amount += product.amount;
    });

    for (const payment of data.payments) {
      if (payment.date) {
        payment.date = payment.date.format('YYYY-MM-DD');
      }
      if (payment.method === 'other') {
        payment.note = payment.note || '';
      } else {
        payment.note = '';
      }
    }
    if (!data.isAddInvoiceInfo) {
      data.invoiceInfo = null;
    }
    const response = await ApiService.put(`/api/orders/${orderId}`, data);
    if (response.status != 200) {
      notification.error({
        message: 'Lỗi khi cập nhật đơn hàng',
      });
      return false;
    }
    navigate('/orders');
  };
  const orderType = Form.useWatch(['type'], form);
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="">
        <Spin spinning={loading}>
          <Title
            level={3}
            className="mb-[30px] flex justify-center items-center sticky top-[60px] bg-white z-10 p-[16px] shadow-md"
          >
            <span>
              {isUpdateOrder
                ? `Chỉnh sửa đơn hàng #${orderId}`
                : orderType == 1
                  ? 'Tạo đơn hàng mới'
                  : orderType == 2
                    ? 'Tạo đơn hàng bảo hành'
                    : 'Tạo đơn hàng cọc'}
            </span>
            {isUpdateOrder && order?.status == 2 && (
              <Tag color="red" className="ml-[10px]">
                Đã hủy
              </Tag>
            )}
          </Title>

          <Row className="px-[16px] max-w-[1368px] mx-auto">
            <Col span={24} offset={0}>
              <Form
                disabled={isUpdateOrder && order?.status == 2}
                form={form}
                layout="vertical"
                // size="large"
                // onFinish={handleFormSubmit}
                scrollToFirstError={true}
                // labelCol={{ span: 8 }}
                // wrapperCol={{ span: 16 }}
                labelAlign="left"
                initialValues={{
                  products: [],
                  vat: 0,
                  totalProductValue: 0,
                  vatAmount: 0,
                  type: 1,
                  additionalFee: 0,
                  payments: [],
                }}
                onValuesChange={onFormValuesChange}
              >
                <Form.Item name="customerId" hidden />
                <Form.Item name="leadId" hidden />
                <Form.Item name="vatAmount" hidden />
                <Form.Item name="outboundRequestId" hidden />
                {/* <Card className="mb-[20px]">
                <Row gutter={[16,16]}></Row>
                <Row gutter={[16,16]}></Row>
              </Card> */}
                {/* Customer Information */}
                <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      <UserPen />
                      THÔNG TIN KHÁCH HÀNG
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                >
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item label="Tìm khách hàng">
                        <AutoComplete
                          placeholder="Nhập sđt/zalo/fb ..."
                          maxLength={10}
                          options={customers.map((item: any) => {
                            return {
                              label: item.leadId ? (
                                <span>
                                  <Tag color="green">Mới</Tag>
                                  {item.name} - {item.phoneNumber}
                                </span>
                              ) : (
                                <span>
                                  {item.name} - {item.phoneNumber}
                                </span>
                              ),
                              value: item.phoneNumber, //item.customerId ? `C_${item.customerId}` : item.leadId ? `L_${item.leadId}` : '',
                              customerId: item.customerId,
                              leadId: item.leadId,
                              name: item.name,
                              address: item.address,
                              phone: item.phoneNumber,
                              source: item.source,
                            };
                          })}
                          onSelect={onSelectCustomer}
                          onSearch={(value: string) => onSearchCustomer(value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col sm={12} xs={24}>
                      <Form.Item
                        label="Số điện thoại"
                        name="customerPhone"
                        rules={[{ required: true, message: 'Không được để trống' }]}
                      >
                        <Input placeholder="VD: 0909383838" />
                      </Form.Item>
                    </Col>
                    <Col sm={12} xs={24}>
                      <Form.Item
                        label="Tên khách hàng"
                        name="customerName"
                        rules={[
                          { required: true, message: 'Không được để trống' },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve(); // required đã check ở trên
                              const words = value.trim().split(/\s+/);
                              if (words.length < 2) {
                                return Promise.reject(new Error('Tên khách hàng phải có ít nhất 2 từ'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input placeholder="VD: Nguyễn Văn A/ Anh Toàn" />
                      </Form.Item>
                    </Col>
                    <Col sm={12} xs={24}>
                      <Form.Item
                        label="Địa chỉ"
                        name="customerAddress"
                        rules={[{ required: false, message: 'Không được để trống' }]}
                      >
                        <Input placeholder="VD: 123 Đường Hoàng Sa, Phường Sài Gòn, TP. HCM" />
                      </Form.Item>
                    </Col>

                    <Col sm={12} xs={24}>
                      <Form.Item label="Nguồn khách hàng" name="customerSource">
                        <Select
                          options={customerSources?.map((item: any) => ({
                            value: item.id,
                            label: item.name,
                          }))}
                          placeholder="VD: Shopee/ Zalo/ Facebook/ v.v..."
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* Order Information */}
                <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      {' '}
                      <CalendarCog />
                      THÔNG TIN ĐƠN HÀNG
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                >
                  <Row gutter={[16, 16]}>
                    <Col sm={12} xs={24}>
                      <Form.Item name="type" label="Loại đơn hàng">
                        <Select>
                          <Select.Option value={1}>Đơn hàng mới</Select.Option>
                          <Select.Option value={2}>Đơn hàng bảo hành</Select.Option>
                          <Select.Option value={3}>Đơn cọc</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    {orderType == 2 && (
                      <Col sm={12} xs={24}>
                        <Form.Item name="refOrderId" label="Đơn hàng tham chiếu">
                          <AutoComplete
                            placeholder="Nhập mã đơn hàng"
                            maxLength={10}
                            options={orders?.map((item: any) => {
                              return {
                                label: item.id,
                                value: item.id,
                                orderDate: item.orderDate,
                              };
                            })}
                            onSearch={searchOrders}
                            onSelect={onSelectOrder}
                            optionRender={(option: any) => {
                              return (
                                <Row>
                                  <Col span={24}>Mã đơn hàng: {option.label}</Col>
                                  <Col span={24}>Ngày đặt hàng: {dayjs(option.orderDate).format('DD/MM/YYYY')}</Col>
                                </Row>
                              );
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item label="Mã xuất kho">
                        <OutboundImportSection
                          outboundInputId={outboundInputId}
                          outboundLoading={outboundLoading}
                          onInputChange={setOutboundInputId}
                          onImport={handleImportOutbound}
                        />
                        <OutboundImportTags tags={importedOutboundTags} onRemoveTag={handleRemoveOutboundTag} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col sm={12} xs={24}>
                      <Form.Item
                        label="Ngày lắp đặt"
                        name="orderDate"
                        initialValue={dayjs()}
                        rules={[{ required: true, message: 'Không được để trống' }]}
                      >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                      </Form.Item>
                    </Col>
                    <Col sm={12} xs={24}>
                      <Input.Group compact>
                        <Form.Item
                          label="Địa chỉ lắp đặt"
                          name="address"
                          rules={[{ required: true, message: 'Không được để trống' }]}
                          style={{
                            display: 'inline-block',
                            width: 'calc(75% - 16px)',
                            marginRight: 16,
                            borderRight: 'none',
                          }}
                        >
                          <Input placeholder="VD: 123 Đường Hoàng Sa, Phường Sài Gòn, TP. HCM" />
                        </Form.Item>
                        <Form.Item
                          label={<></>}
                          name="intercity"
                          valuePropName="checked"
                          style={{
                            display: 'inline-block',
                            width: 'calc(25% - 16px)',
                            marginBottom: 0,
                            borderRight: 'none',
                          }}
                        >
                          <Checkbox>Đi tỉnh</Checkbox>
                        </Form.Item>
                      </Input.Group>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col sm={12} xs={24}>
                      <Form.Item label="Sale phụ trách chính" name="saleId">
                        <Select
                          placeholder="Chọn sale"
                          options={sales?.map((sale: SaleInfo) => ({
                            label: sale.fullName,
                            value: sale.id,
                            status: sale.status,
                            searchname: removeAccentCharacters(sale.fullName).toLowerCase(),
                          }))}
                          showSearch
                          filterOption={onSearchSale}
                        />
                      </Form.Item>
                    </Col>
                    <Col sm={12} xs={24}>
                      <Form.Item
                        label="Kỹ thuật viên phụ trách"
                        name="workers"
                        rules={[{ required: true, message: 'Không được để trống' }]}
                      >
                        <Select
                          options={workers
                            ?.filter((worker: WorkerInfo) => worker.active == 1)
                            .map((worker: WorkerInfo) => ({
                              label: worker.name,
                              value: worker.id,
                              active: worker.active,
                              searchname: removeAccentCharacters(worker.name).toLowerCase(),
                            }))}
                          showSearch
                          filterOption={onSearchWorker}
                          mode="multiple"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Sale hỗ trợ" name="sharedSales">
                        <Select
                          placeholder="Chọn sale chia sẻ"
                          options={sales?.map((sale: SaleInfo) => ({
                            label: sale.fullName,
                            value: sale.id,
                            status: sale.status,
                            searchname: removeAccentCharacters(sale.fullName).toLowerCase(),
                          }))}
                          mode="multiple"
                          showSearch
                          filterOption={onSearchSale}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Khách hàng giới thiệu" name="referralCustomer">
                        <Input placeholder="VD: Nguyễn Văn B -  0909383838" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Sàn TMĐT/ĐVVC"
                        name="fulfillmentMethod"
                        dependencies={['fulfillmentCode']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const fulfillmentCode = getFieldValue('fulfillmentCode');
                              if (fulfillmentCode && !value) {
                                return Promise.reject(
                                  new Error('Vui lòng chọn Sàn TMĐT/ĐVVC khi đã nhập mã đơn hàng/mã vận đơn'),
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select placeholder="Chọn Ecom/3PL" options={fulfillmentMethodOptions} showSearch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Mã đơn hàng/mã vận đơn"
                        name="fulfillmentCode"
                        dependencies={['fulfillmentMethod']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const fulfillmentMethod = getFieldValue('fulfillmentMethod');
                              if (fulfillmentMethod && !value) {
                                return Promise.reject(
                                  new Error('Vui lòng nhập mã đơn hàng/mã vận đơn khi đã chọn Sàn TMĐT/ĐVVC'),
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input placeholder="VD: VN138841919/GHN193919/GHTK193919" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item label="Ghi chú" name="note">
                        <TextArea rows={4} placeholder="Lưu ý: nhập các thông tin liên quan đến đơn hàng" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
                <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      <ShoppingBasket /> THÔNG TIN SẢN PHẨM
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                >
                  {/* Product Table */}
                  <Form.List
                    name="products"
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!hasAtLeastOneProduct(value)) {
                            return Promise.reject(new Error('Đơn hàng thiếu sản phẩm'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    {(fields, { add, remove }) => {
                      // Update expandedRowKeys to use field keys instead of indices
                      // This ensures workItems are preserved when products are removed
                      const fieldKeys = fields.map(field => field.key);
                      const currentKeysSet = new Set<React.Key>(expandedRowKeys);
                      const newKeysSet = new Set<React.Key>(fieldKeys);
                      const keysChanged =
                        fieldKeys.length !== expandedRowKeys.length ||
                        fieldKeys.some(key => !currentKeysSet.has(key)) ||
                        expandedRowKeys.some(key => !newKeysSet.has(key));

                      if (keysChanged) {
                        setExpandedRowKeys([...fieldKeys]);
                      }

                      const handleRemove = (index: number) => {
                        remove(index);
                      };
                      const dataSource = fields.map((field, index) => ({
                        name: field.name,
                        key: field.key,
                        index,
                      })) as any;
                      return (
                        <div className="">
                          <Table
                            columns={productColumns(fields, handleRemove)}
                            dataSource={dataSource}
                            rowKey="key"
                            pagination={false}
                            className="mb-[20px]"
                            rowClassName={() => '[&>td]:!align-top [&>td]:!pb-6 [&>td]:!px-2'}
                            footer={() => (
                              <Row>
                                <Col>
                                  <Button
                                    type="dashed"
                                    block
                                    icon={<PlusOutlined />}
                                    onClick={() => add({ workItems: [] })}
                                    className="bg-green-100 hover:!bg-green-200"
                                  >
                                    Thêm sản phẩm
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            tableLayout="fixed"
                            expandable={{
                              expandedRowRender: (record: any) => {
                                return (
                                  <Form.List name={[record.name, 'workItems']}>
                                    {(itemFields, { add: addItem, remove: removeItem }) => {
                                      return (
                                        <Table
                                          size="small"
                                          dataSource={itemFields}
                                          locale={{ emptyText: <></> }}
                                          columns={[
                                            {
                                              title: 'Hạng mục',
                                              // width: 400,
                                              render: (_, itemField, index) => (
                                                <Form.Item
                                                  name={[itemField.name, 'workItemId']}
                                                  style={{ marginBottom: 0, width: '100%' }}
                                                >
                                                  <Select
                                                    options={workItems?.map((workItem: any) => ({
                                                      label: workItem.name,
                                                      value: workItem.id,
                                                      active: workItem.active,
                                                      estimateCost: workItem.estimateCost,
                                                      unitNum: workItem.unitNum,
                                                      searchname: removeAccentCharacters(workItem.name).toLowerCase(),
                                                    }))}
                                                    showSearch
                                                    popupMatchSelectWidth={false}
                                                    filterOption={onSearchWorkItem}
                                                    onSelect={(value, option) =>
                                                      onSelectWorkItem(record.name, index, value, option)
                                                    }
                                                    style={{ width: '100%' }}
                                                  />
                                                </Form.Item>
                                              ),
                                            },
                                            {
                                              title: 'Công',
                                              width: 100,
                                              render: (_, itemField) => (
                                                <Form.Item
                                                  name={[itemField.name, 'coefficient']}
                                                  style={{ marginBottom: 0 }}
                                                >
                                                  <InputNumber
                                                    placeholder="Hệ số"
                                                    step={0.1}
                                                    min={0}
                                                    className="w-full"
                                                  />
                                                </Form.Item>
                                              ),
                                            },
                                            {
                                              title: 'Đơn vị',
                                              width: 170,
                                              render: (_, itemField) => (
                                                <Form.Item name={[itemField.name, 'unit']} style={{ marginBottom: 0 }}>
                                                  <Select options={workItemUnitOptions} popupMatchSelectWidth={false} />
                                                </Form.Item>
                                              ),
                                            },
                                            {
                                              title: 'Thợ lắp',
                                              // width: 300,
                                              render: (_, itemField) => {
                                                const selectedWorkerIds = workersSelected;
                                                const filteredOptions = (workers || [])
                                                  .filter((w: WorkerInfo) => selectedWorkerIds.includes(w.id))
                                                  .map((worker: WorkerInfo) => ({
                                                    label: worker.name,
                                                    value: worker.id,
                                                    active: worker.active,
                                                    searchname: removeAccentCharacters(worker.name).toLowerCase(),
                                                  }));
                                                return (
                                                  <Form.Item
                                                    name={[itemField.name, 'workerId']}
                                                    style={{ marginBottom: 0 }}
                                                  >
                                                    <Select
                                                      options={filteredOptions}
                                                      showSearch
                                                      filterOption={onSearchWorker}
                                                      popupMatchSelectWidth={false}
                                                    />
                                                  </Form.Item>
                                                );
                                              },
                                            },
                                            {
                                              title: '',
                                              width: 70,
                                              render: (_, __, index) => (
                                                <Button
                                                  type="text"
                                                  icon={<OctagonMinus color="orange" />}
                                                  onClick={() => removeItem(index)}
                                                />
                                              ),
                                            },
                                          ]}
                                          tableLayout="fixed"
                                          pagination={false}
                                          footer={() => (
                                            <Row>
                                              <Col>
                                                <Button
                                                  type="dashed"
                                                  block
                                                  icon={<PlusOutlined />}
                                                  onClick={() =>
                                                    addItem({
                                                      workerId: workersSelected.length == 1 ? workersSelected[0] : null,
                                                    })
                                                  }
                                                  className="bg-yellow-100 hover:!bg-yellow-200"
                                                >
                                                  Thêm công
                                                </Button>
                                              </Col>
                                            </Row>
                                          )}
                                        />
                                      );
                                    }}
                                  </Form.List>
                                );
                              },
                              expandedRowKeys,
                              onExpandedRowsChange: keys => {
                                setExpandedRowKeys([...keys]);
                              },
                              showExpandColumn: false,
                            }}
                          />
                        </div>
                      );
                    }}
                  </Form.List>
                  <Row gutter={[16, 16]}>
                    <Col sm={12} xs={24}></Col>
                    <Col sm={12} xs={24}>
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right' }}>
                          Tổng giá bán trước thuế
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right' }}>
                          {formatCurrency(orderFinancialInfo.totalProductValue - orderFinancialInfo.totalVatAmount)}
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right' }}>
                          Thuế GTGT
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right' }}>
                          {formatCurrency(orderFinancialInfo.totalVatAmount)}
                        </Col>
                        <Divider className="my-2" />
                      </Row>
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Tổng giá bán sau thuế
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          {formatCurrency(orderFinancialInfo.totalProductValue)}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
                {/* Invoice Information */}
                <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      <DollarSign />
                      THÔNG TIN CHI PHÍ
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                >
                  <Row gutter={[16, 16]} style={{ marginBottom: '10px' }}>
                    <Col span={24}>
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          {[1, 7].includes(user.role) && (
                            <Row>
                              <Col span={4}>
                                <Form.Item name="vat" valuePropName="checked">
                                  <Checkbox>FD</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item name="invoiceIssued">
                                  <Select>
                                    <Select.Option value={0}>Chưa xuất</Select.Option>
                                    <Select.Option value={1}>Đã xuất</Select.Option>
                                    <Select.Option value={2}>Đã xuất & điều chỉnh</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>
                          )}
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="isAddInvoiceInfo"
                            rules={[{ required: true, message: 'Vui lòng chọn tùy chọn hóa đơn VAT' }]}
                          >
                            <Radio.Group>
                              <Radio value={true}>KH lấy hóa đơn VAT</Radio>
                              <Radio value={false}>
                                KH <b>KHÔNG</b> lấy hóa đơn VAT
                              </Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item noStyle shouldUpdate={true}>
                        {({ getFieldValue }) =>
                          getFieldValue('isAddInvoiceInfo') && (
                            <>
                              <Row gutter={[16, 0]}>
                                <Col sm={12} xs={24}>
                                  <Form.Item
                                    name={['invoiceInfo', 'taxCode']}
                                    label="Mã số thuế"
                                    dependencies={['isAddInvoiceInfo']}
                                    rules={[
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const isAddInvoiceInfo = getFieldValue('isAddInvoiceInfo');
                                          if (isAddInvoiceInfo && !value) {
                                            return Promise.reject(new Error('Vui lòng nhập mã số thuế'));
                                          }
                                          return Promise.resolve();
                                        },
                                      }),
                                      { max: 20, message: 'Mã số thuế tối đa 20 ký tự' },
                                    ]}
                                  >
                                    <Input placeholder="Mã số thuế" maxLength={20} />
                                  </Form.Item>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item
                                    name={['invoiceInfo', 'name']}
                                    label="Tên công ty/khách hàng"
                                    dependencies={['isAddInvoiceInfo']}
                                    rules={[
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const isAddInvoiceInfo = getFieldValue('isAddInvoiceInfo');
                                          if (isAddInvoiceInfo && !value) {
                                            return Promise.reject(new Error('Vui lòng nhập tên công ty/khách hàng'));
                                          }
                                          return Promise.resolve();
                                        },
                                      }),
                                    ]}
                                  >
                                    <Input placeholder="Tên công ty/khách hàng" />
                                  </Form.Item>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item name={['invoiceInfo', 'phone']} label="Số điện thoại">
                                    <Input placeholder="Số điện thoại" />
                                  </Form.Item>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item
                                    name={['invoiceInfo', 'address']}
                                    label="Địa chỉ"
                                    dependencies={['isAddInvoiceInfo']}
                                    rules={[
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const isAddInvoiceInfo = getFieldValue('isAddInvoiceInfo');
                                          if (isAddInvoiceInfo && !value) {
                                            return Promise.reject(new Error('Vui lòng nhập địa chỉ'));
                                          }
                                          return Promise.resolve();
                                        },
                                      }),
                                    ]}
                                  >
                                    <Input placeholder="Địa chỉ" />
                                  </Form.Item>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item name={['invoiceInfo', 'email']} label="Email">
                                    <Input placeholder="Email" />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </>
                          )
                        }
                      </Form.Item>
                    </Col>
                    {/* <Col span={24}>
                      <Form.Item noStyle shouldUpdate={true}>
                        {({ getFieldValue }) =>
                          getFieldValue('isVAT') && (
                            <>
                              <Form.Item label="VAT">
                                <Input.Group compact>
                                  <Form.Item name="vat" noStyle>
                                    <InputNumber
                                      style={{ width: '50%' }}
                                      // onChange={value => setInvoiceInfo({ ...invoiceInfo, vat: value || 0 })}
                                      controls={false}
                                      min={0}
                                      max={100}
                                      suffix="%"
                                    />
                                  </Form.Item>
                                  <Form.Item name="vatAmount" noStyle>
                                    <InputNumber
                                      style={{ width: '50%' }}
                                      formatter={(value?: number | string) =>
                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                      }
                                      parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                                      // onChange={value => setInvoiceInfo({ ...invoiceInfo, vat: value || 0 })}
                                      controls={false}
                                      readOnly
                                    />
                                  </Form.Item>
                                </Input.Group>
                              </Form.Item>
                            </>
                          )
                        }
                      </Form.Item>
                    </Col> */}
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        label="Số tiền chiết khấu"
                        name="commissionFee"
                        className="[&_.ant-input-number-input]:!text-right"
                      >
                        <InputNumber
                          className=" w-full"
                          controls={false}
                          formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                          addonAfter="₫"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Hoàn vật tư/ship lấy hàng"
                        name="operationFee"
                        className="[&_.ant-input-number-input]:!text-right"
                      >
                        <InputNumber
                          className="w-full"
                          controls={false}
                          formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                          addonAfter="₫"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Hoàn tip cho KTV"
                        name="ktvTip"
                        className="[&_.ant-input-number-input]:!text-right"
                      >
                        <InputNumber
                          className="w-full"
                          controls={false}
                          formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                          addonAfter="₫"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '10px' }}>
                    <Col span={24}>
                      <Form.Item label="Phụ thu thêm">
                        <Input.Group compact>
                          <Form.Item name="additionalFeeDescription" noStyle>
                            <Input
                              style={{ width: '50%' }}
                              placeholder="Nhập mô tả phụ phí"
                              className="!rounded-none border-r-0 flex-1"
                            />
                          </Form.Item>
                          <Form.Item name="additionalFee" noStyle>
                            <InputNumber
                              style={{ width: '50%' }}
                              controls={false}
                              className="
                                [&_.ant-input-number]:rounded-l-none
                                [&_.ant-input-number-group-addon]:rounded-l-none
                                [&_.ant-input-number-input]:text-right
                              "
                              formatter={(value?: number | string) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(value?: string) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                              addonAfter="₫"
                              onBeforeInput={(e: any) => {
                                // React SyntheticEvent => e.data là ký tự người dùng sắp nhập
                                if (e.data && !/^[0-9]$/.test(e.data)) {
                                  e.preventDefault();
                                }
                              }}
                              onKeyDown={e => {
                                // Ngăn nhập các ký tự điều khiển đặc biệt như e, +, -, .
                                const invalidKeys = ['e', 'E', '+', '-', '.'];
                                if (invalidKeys.includes(e.key)) e.preventDefault();
                              }}
                              onPaste={e => {
                                // Chặn dán nếu có ký tự không phải số
                                const text = e.clipboardData.getData('text');
                                if (!/^\d+$/.test(text)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </Form.Item>
                        </Input.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '10px' }}>
                    <Col sm={12} xs={24}></Col>
                    <Col sm={12} xs={24}>
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Giá trị sản phẩm (Đã gồm VAT)
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          <Form.Item noStyle shouldUpdate={true}>
                            {({ getFieldValue }) => {
                              return <>{formatCurrency(getFieldValue('totalProductValue'))}</>;
                            }}
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Doanh thu NET
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          <Form.Item noStyle shouldUpdate={true}>
                            {({ getFieldValue }) => {
                              const totalProductValue = getFieldValue('totalProductValue') || 0;
                              const vatAmount = getFieldValue('vatAmount') || 0;
                              const commissionFee = getFieldValue('commissionFee') || 0;
                              return <>{formatCurrency(totalProductValue - vatAmount - commissionFee)}</>;
                            }}
                          </Form.Item>
                        </Col>
                      </Row>
                      {/* <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Công thợ
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          <Form.Item noStyle shouldUpdate={true}>
                            {({ getFieldValue }) => {
                              let totalWorkerCost = 0;
                              getFieldValue('products').forEach((product: any) => {
                                product.workItems.forEach((workItem: any) => {
                                  totalWorkerCost += Number(workItem.cost || 0);
                                });
                              });
                              return <>{formatCurrency(totalWorkerCost)}</>;
                            }}
                          </Form.Item>
                        </Col>
                      </Row> */}
                      <Divider className="my-2" />
                      <Row>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold', color: '#52c41a' }}>
                          Tổng giá trị đơn hàng
                        </Col>
                        <Col sm={12} xs={12} style={{ textAlign: 'right', fontWeight: 'bold', color: '#52c41a' }}>
                          {formatCurrency(orderFinancialInfo.grossAmount)}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>

                {/* Payment Information */}
                <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      <CreditCard />
                      THÔNG TIN THANH TOÁN
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                >
                  <Form.List name="payments">
                    {(fields, { add, remove }) => {
                      const dataSource = fields.map((field, index) => ({
                        key: field.key,
                        index,
                      })) as any;
                      return (
                        <Table
                          columns={paymentColumns(fields, remove)}
                          dataSource={dataSource}
                          pagination={false}
                          className="mb-[20px]"
                          footer={() => (
                            <Row>
                              <Col>
                                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add()}>
                                  Thêm lần thanh toán
                                </Button>
                              </Col>
                            </Row>
                          )}
                          tableLayout="fixed"
                        />
                      );
                    }}
                  </Form.List>
                </Card>

                {/* Additional Information */}
                {/* <Card
                  title={
                    <span style={{ color: '#52c41a' }} className="flex">
                      <ListPlus />
                      THÔNG TIN BỔ SUNG
                    </span>
                  }
                  bordered={false}
                  className="mb-[20px]"
                ></Card> */}

                {/* Change Log */}
                {isUpdateOrder && orderChangedLog?.length > 0 && [1].includes(user.role) && (
                  <Card
                    title={
                      <span style={{ color: '#52c41a' }} className="flex">
                        <ListPlus />
                        LỊCH SỬ THAY ĐỔI
                      </span>
                    }
                    bordered={false}
                    className="mb-[20px]"
                  >
                    {(() => {
                      const changeLog = orderChangedLog || [];
                      return (
                        <div className="space-y-6">
                          {changeLog.map((logEntry: any, logIndex: number) => (
                            <div
                              key={logEntry.id || logIndex}
                              className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0"
                            >
                              {/* Log Entry Header */}
                              <Row gutter={[16, 8]} className="mb-3">
                                <Col span={24}>
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <Text strong>{logEntry.changedByName || 'Người dùng'}</Text>
                                      <Text type="secondary" className="ml-2 text-xs">
                                        {logEntry.createdAt ? dayjs(logEntry.createdAt).format('DD/MM/YYYY HH:mm') : ''}
                                      </Text>
                                    </div>
                                    {logEntry.summary && (
                                      <Text type="secondary" className="text-xs">
                                        {logEntry.summary}
                                      </Text>
                                    )}
                                  </div>
                                  {logEntry.note && (
                                    <div className="mt-2">
                                      <Text type="secondary" className="text-xs italic">
                                        Ghi chú: {logEntry.note}
                                      </Text>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                              {/* Changed List */}
                              <div className="space-y-3 ml-4">
                                {logEntry.changed && logEntry.changed.length > 0 ? (
                                  logEntry.changed.map((change: any, changeIndex: number) => {
                                    const fieldLabel = change.fieldLabel || change.field;

                                    // Helper function to render item details
                                    const renderItemDetails = (item: any, isDeleted: boolean = false) => {
                                      if (!item) return null;
                                      return (
                                        <div className="mt-2 ml-4 space-y-1">
                                          {Object.entries(item).map(([key, value]: [string, any]) => {
                                            // Skip internal fields and show formatted versions when available
                                            if (key.endsWith('Formatted') || key === 'id' || key === 'orderId') {
                                              return null;
                                            }
                                            const formattedKey = `${key}Formatted`;
                                            const displayValue =
                                              item[formattedKey] !== undefined ? item[formattedKey] : value;
                                            const displayLabel =
                                              key === 'date'
                                                ? 'Ngày'
                                                : key === 'amount'
                                                  ? 'Số tiền'
                                                  : key === 'method'
                                                    ? 'Phương thức'
                                                    : key === 'note'
                                                      ? 'Ghi chú'
                                                      : key;
                                            return (
                                              <Row key={key} gutter={[8, 0]}>
                                                <Col span={6}>
                                                  <Text type="secondary" className="text-xs">
                                                    {displayLabel}:
                                                  </Text>
                                                </Col>
                                                <Col span={18}>
                                                  {isDeleted ? (
                                                    <Text delete className="text-xs">
                                                      {displayValue || '-'}
                                                    </Text>
                                                  ) : (
                                                    <Text className="text-xs">{displayValue || '-'}</Text>
                                                  )}
                                                </Col>
                                              </Row>
                                            );
                                          })}
                                        </div>
                                      );
                                    };

                                    // Check if this is an array change (has nested "changed" array)
                                    if (change.changed && Array.isArray(change.changed)) {
                                      return (
                                        <div key={changeIndex} className="border-l-2 border-blue-300 pl-4">
                                          <Row gutter={[16, 8]} align="middle" className="mb-2">
                                            <Col flex="auto">
                                              <Text strong>{fieldLabel}:</Text>
                                            </Col>
                                          </Row>
                                          <div className="ml-4 space-y-3">
                                            {change.changed.map((nestedChange: any, nestedIndex: number) => {
                                              const nestedType = nestedChange.type;
                                              const nestedFieldLabel = nestedChange.fieldLabel || nestedChange.field;

                                              // Handle "added" type in array changes
                                              if (nestedType === 'added') {
                                                return (
                                                  <div key={nestedIndex} className="border-l-2 border-green-300 pl-4">
                                                    <Row gutter={[16, 8]} align="middle">
                                                      <Col flex="auto">
                                                        <Text strong style={{ color: '#52c41a' }}>
                                                          {nestedChange.displayText || `Đã thêm ${nestedFieldLabel}`}
                                                        </Text>
                                                      </Col>
                                                    </Row>
                                                    {renderItemDetails(nestedChange.item, false)}
                                                  </div>
                                                );
                                              }

                                              // Handle "removed" type in array changes
                                              if (nestedType === 'removed') {
                                                return (
                                                  <div key={nestedIndex} className="border-l-2 border-red-300 pl-4">
                                                    <Row gutter={[16, 8]} align="middle">
                                                      <Col flex="auto">
                                                        <Text strong style={{ color: '#ff4d4f' }}>
                                                          {nestedChange.displayText || `Đã xóa ${nestedFieldLabel}`}
                                                        </Text>
                                                      </Col>
                                                    </Row>
                                                    {renderItemDetails(nestedChange.item, true)}
                                                  </div>
                                                );
                                              }

                                              // Handle "changed" type in array changes
                                              if (nestedType === 'changed' && nestedChange.item) {
                                                return (
                                                  <div key={nestedIndex} className="border-l-2 border-orange-300 pl-4">
                                                    <Row gutter={[16, 8]} align="middle">
                                                      <Col flex="auto">
                                                        <Text strong>
                                                          {nestedChange.displayText ||
                                                            `Đã thay đổi ${nestedFieldLabel}`}
                                                        </Text>
                                                      </Col>
                                                    </Row>
                                                    {nestedChange.item && (
                                                      <div className="mt-2 ml-4 space-y-1">
                                                        {Object.entries(nestedChange.item).map(
                                                          ([key, value]: [string, any]) => {
                                                            if (
                                                              key.endsWith('Formatted') ||
                                                              key === 'id' ||
                                                              key === 'orderId'
                                                            ) {
                                                              return null;
                                                            }
                                                            const formattedKey = `${key}Formatted`;
                                                            const beforeKey = `before${key.charAt(0).toUpperCase() + key.slice(1)}Formatted`;
                                                            const afterKey = `after${key.charAt(0).toUpperCase() + key.slice(1)}Formatted`;

                                                            const beforeValue =
                                                              nestedChange[beforeKey] ||
                                                              nestedChange.item[`before${formattedKey}`];
                                                            const afterValue =
                                                              nestedChange[afterKey] ||
                                                              nestedChange.item[`after${formattedKey}`] ||
                                                              nestedChange.item[formattedKey] ||
                                                              value;

                                                            const displayLabel =
                                                              key === 'date'
                                                                ? 'Ngày'
                                                                : key === 'amount'
                                                                  ? 'Số tiền'
                                                                  : key === 'method'
                                                                    ? 'Phương thức'
                                                                    : key === 'note'
                                                                      ? 'Ghi chú'
                                                                      : key;

                                                            if (beforeValue !== undefined || afterValue !== undefined) {
                                                              return (
                                                                <Row key={key} gutter={[8, 0]} className="mb-1">
                                                                  <Col span={6}>
                                                                    <Text type="secondary" className="text-xs">
                                                                      {displayLabel}:
                                                                    </Text>
                                                                  </Col>
                                                                  <Col span={9}>
                                                                    <Space>
                                                                      <Text type="secondary" className="text-xs">
                                                                        Trước:
                                                                      </Text>
                                                                      <Text delete className="text-xs">
                                                                        {beforeValue || '-'}
                                                                      </Text>
                                                                    </Space>
                                                                  </Col>
                                                                  <Col span={9}>
                                                                    <Space>
                                                                      <Text type="secondary" className="text-xs">
                                                                        Sau:
                                                                      </Text>
                                                                      <Text
                                                                        style={{ color: '#52c41a' }}
                                                                        className="text-xs"
                                                                      >
                                                                        {afterValue || '-'}
                                                                      </Text>
                                                                    </Space>
                                                                  </Col>
                                                                </Row>
                                                              );
                                                            }
                                                            return null;
                                                          },
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              }

                                              // Fallback for unknown nested change types
                                              return (
                                                <div key={nestedIndex} className="border-l-2 border-gray-300 pl-4">
                                                  <Text type="secondary" className="text-xs">
                                                    {nestedChange.displayText || 'Thay đổi không xác định'}
                                                  </Text>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Handle simple field changes (has before/after)
                                    if (change.before !== undefined || change.after !== undefined) {
                                      return (
                                        <div key={changeIndex} className="border-l-2 border-gray-300 pl-4">
                                          <Row gutter={[16, 8]} align="middle">
                                            <Col flex="auto">
                                              <Text strong>{fieldLabel}:</Text>
                                            </Col>
                                            <Col>
                                              <Space>
                                                <Text type="secondary" className="text-xs">
                                                  Trước:
                                                </Text>
                                                <Text delete={change.before !== null && change.after !== null}>
                                                  {change.beforeFormatted || change.before}
                                                </Text>
                                              </Space>
                                            </Col>
                                            <Col>
                                              <Space>
                                                <Text type="secondary" className="text-xs">
                                                  Sau:
                                                </Text>
                                                <Text style={{ color: '#52c41a' }}>
                                                  {change.afterFormatted || change.after}
                                                </Text>
                                              </Space>
                                            </Col>
                                          </Row>
                                        </div>
                                      );
                                    }

                                    // Fallback for unknown change types
                                    return (
                                      <div key={changeIndex} className="border-l-2 border-gray-300 pl-4">
                                        <Text type="secondary" className="text-xs">
                                          {fieldLabel}: Thay đổi không xác định
                                        </Text>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <Text type="secondary" className="text-xs">
                                    Không có thay đổi
                                  </Text>
                                )}
                              </div>
                            </div>
                          ))}
                          {changeLog.length === 0 && <Text type="secondary">Không có thay đổi nào</Text>}
                        </div>
                      );
                    })()}
                  </Card>
                )}
                {/* Actions */}
              </Form>
            </Col>
          </Row>
        </Spin>
        <OutboundImportModal
          open={outboundModalOpen}
          pendingOutboundId={pendingOutboundId}
          items={outboundModalItems}
          existingSkuQuantities={existingSkuQuantities}
          isDuplicateOutboundSku={isDuplicateOutboundSku}
          onClose={() => setOutboundModalOpen(false)}
          onConfirm={handleConfirmOutboundImport}
          onToggleItem={(key, selected) => {
            setOutboundModalItems((prev: OutboundModalItem[]) =>
              prev.map((item: OutboundModalItem) => (item.key === key ? { ...item, selected } : item)),
            );
          }}
        />
        <div className="sticky bottom-[0px] z-10 text-right p-[10px] bg-[#fdfdfd] mb-[-20px] shadow-[0_-4px_10px_rgba(0,0,0,0.08)]">
          <Space>
            <Button onClick={() => backToOrderPage()} disabled={false}>
              Quay lại
            </Button>
            {!isUpdateOrder ? (
              <>
                <Button type="primary" style={{ backgroundColor: '#52c41a' }} onClick={onSubmitAndCreate}>
                  Lưu và tạo mới
                </Button>
                <Button type="primary" htmlType="submit" onClick={onSubmit}>
                  Lưu
                </Button>
              </>
            ) : (
              <>
                <Button type="primary" danger onClick={onCancelOrder}>
                  Hủy đơn hàng
                </Button>
                <Button type="primary" htmlType="submit" onClick={onUpdateOrder}>
                  Cập nhật
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

export default OrderCreationForm;
